import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowLeft, FaBuilding, FaCalendarAlt, FaExternalLinkAlt } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import logo from "../assets/logo.png";

const SOCIAL_API_BASE = "https://step-in.onrender.com/api";

// Format date to readable format (e.g., "Jan 1, 2026")
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Get Apply button state based on job status and dates
const getApplyButtonState = (job) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Check status first
  if (job.status?.toLowerCase() === "closed") {
    return { disabled: true, text: "Applications Closed" };
  }
  if (job.status?.toLowerCase() === "paused") {
    return { disabled: true, text: "Applications Paused" };
  }

  // Check dates
  if (job.application_start_date) {
    const startDate = new Date(job.application_start_date);
    if (today < startDate) {
      return { disabled: true, text: "Applications Not Yet Open" };
    }
  }

  if (job.application_end_date) {
    const endDate = new Date(job.application_end_date);
    if (today > endDate) {
      return { disabled: true, text: "Application Deadline Passed" };
    }
  }

  // Default: enabled
  return { disabled: false, text: "Apply Now" };
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    open: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    closed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  };

  const style = statusStyles[status?.toLowerCase()] || statusStyles.paused;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${style}`}>
      {status || "Unknown"}
    </span>
  );
};

// Job Card component for listing view
const JobCard = ({ job, isDarkMode, onClick }) => {
  return (
    <motion.div
      onClick={onClick}
      className={`p-5 rounded-xl border cursor-pointer transition-all duration-200 ${
        isDarkMode
          ? "bg-gray-800 border-gray-700 hover:bg-gray-750 hover:border-gray-600"
          : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"
      }`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {job.job_title}
          </h3>
          <div className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            <FaBuilding className="h-3 w-3" />
            <span>{job.company_name}</span>
          </div>
        </div>
        <StatusBadge status={job.status} />
      </div>
      <div className={`mt-3 flex items-center gap-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
        <div className="flex items-center gap-1">
          <FaCalendarAlt className="h-3 w-3" />
          <span>{formatDate(job.application_start_date)} - {formatDate(job.application_end_date)}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Job Detail view component
const JobDetail = ({ socialId, isDarkMode, onBack }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${SOCIAL_API_BASE}/social-jobs/${socialId}/`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Job not found");
          }
          throw new Error("Failed to fetch job details");
        }
        const data = await response.json();
        setJob(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (socialId) {
      fetchJobDetail();
    }
  }, [socialId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-opacity-20"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent border-l-transparent border-indigo-500 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-20 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        <div className={`p-6 rounded-xl border ${isDarkMode ? "bg-red-900/30 border-red-800" : "bg-red-100 border-red-200"}`}>
          <p className="text-lg font-medium">{error}</p>
          <button
            onClick={onBack}
            className={`mt-4 px-4 py-2 rounded-lg font-medium ${
              isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
            }`}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`max-w-2xl mx-auto p-6 rounded-2xl border ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <StatusBadge status={job.status} />
      </div>

      <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        {job.job_title}
      </h1>

      <div className={`flex items-center gap-2 text-lg mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        <FaBuilding className="h-4 w-4" />
        <span>{job.company_name}</span>
      </div>

      <div className={`space-y-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        <div className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
          <h3 className="font-semibold mb-2">Application Period</h3>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="h-4 w-4" />
            <span>
              {formatDate(job.application_start_date)} - {formatDate(job.application_end_date)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        {/* Apply Now Button */}
        {(() => {
          const buttonState = getApplyButtonState(job);
          return (
            <button
              onClick={() => {
                if (!buttonState.disabled && job.application_link) {
                  window.open(job.application_link, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={buttonState.disabled || !job.application_link}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                buttonState.disabled || !job.application_link
                  ? isDarkMode
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : isDarkMode
                    ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                    : "bg-indigo-500 hover:bg-indigo-600 text-white"
              }`}
            >
              {!buttonState.disabled && <FaExternalLinkAlt className="h-4 w-4" />}
              {buttonState.text}
            </button>
          );
        })()}

        {/* Back Button */}
        <button
          onClick={onBack}
          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
            isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
          }`}
        >
          Back to Jobs
        </button>
      </div>
    </motion.div>
  );
};

// Main SocialJobView component
const SocialJobView = ({ isDarkMode, onBack }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const socialId = searchParams.get("social_id");

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all jobs for listing view
  useEffect(() => {
    if (!socialId) {
      const fetchJobs = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(`${SOCIAL_API_BASE}/social-jobs/`);
          if (!response.ok) {
            throw new Error("Failed to fetch jobs");
          }
          const data = await response.json();
          // Handle null, undefined, or non-array responses
          setJobs(Array.isArray(data) ? data : []);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchJobs();
    }
  }, [socialId]);

  const handleJobClick = (job) => {
    setSearchParams({ social_id: job.social_id });
  };

  const handleBackToListing = () => {
    setSearchParams({});
  };

  // Render detail view if social_id is present
  if (socialId) {
    return (
      <motion.section
        className={`min-h-screen py-20 px-6 transition-colors duration-500 ${
          isDarkMode ? "bg-gray-900 text-gray-200" : "bg-gray-50 text-gray-800"
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="max-w-6xl mx-auto">
          <motion.button
            onClick={handleBackToListing}
            className={`flex items-center gap-2 font-medium mb-8 px-5 py-2.5 rounded-full shadow-sm border backdrop-blur-sm transition-all duration-200 ${
              isDarkMode
                ? "text-gray-300 border-gray-700 hover:bg-gray-800 hover:text-white"
                : "text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
            }`}
            whileHover={{ x: -5 }}
          >
            <FaArrowLeft />
            Back to Jobs
          </motion.button>

          <JobDetail socialId={socialId} isDarkMode={isDarkMode} onBack={handleBackToListing} />
        </div>
      </motion.section>
    );
  }

  // Render listing view
  return (
    <motion.section
      className={`min-h-screen py-20 px-6 transition-colors duration-500 ${
        isDarkMode ? "bg-gray-900 text-gray-200" : "bg-gray-50 text-gray-800"
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <motion.button
          onClick={onBack}
          className={`flex items-center gap-2 font-medium mb-8 px-5 py-2.5 rounded-full shadow-sm border backdrop-blur-sm transition-all duration-200 ${
            isDarkMode
              ? "text-gray-300 border-gray-700 hover:bg-gray-800 hover:text-white"
              : "text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
          }`}
          whileHover={{ x: -5 }}
        >
          <FaArrowLeft />
          Back to Home
        </motion.button>

        {/* Header */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-pink-500">
            Social Job View
          </h2>
          <p className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Browse and share job opportunities
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-opacity-20"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent border-l-transparent border-indigo-500 absolute top-0 left-0"></div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className={`p-6 rounded-xl border text-center ${isDarkMode ? "bg-red-900/30 border-red-800" : "bg-red-100 border-red-200"}`}>
            <p className="text-lg font-medium">{error}</p>
          </div>
        )}

        {/* Empty State - Coming Soon */}
        {!loading && !error && jobs.length === 0 && (
          <div className={`p-10 rounded-2xl border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <div className="flex flex-col items-center justify-center py-10">
               <img
                      src={isDarkMode ? require('../assets/logo.png') : "https://jobx-image.s3.ap-south-1.amazonaws.com/Group+166.svg"}
                      alt="JobxBharat"
                      className="h-12 w-12 object-contain"
                    />
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Coming Soon
              </h3>
              <p className={`text-lg mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                We're working on bringing you exciting job opportunities.
              </p>
              <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                Stay tuned for updates!
              </p>
            </div>
          </div>
        )}

        {/* Job Listing */}
        {!loading && !error && jobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isDarkMode={isDarkMode}
                onClick={() => handleJobClick(job)}
              />
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default SocialJobView;
